import { HtmlInFrame, HtmlInFrameHandle } from "client/Components";
import { useForceRerender } from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { adjustTargetAndRelForAnchors, sanitizeHtml } from "client/Misc/htmlUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import styles from "./Body.module.less";
import { convertPlainTextBodyToHtml } from "client/Features/Email/utils";
import { ExchangeMessageBodyKind } from "client/Api/Email/common";
import { newGuid } from "client_legacy/ndaw/guid";

export type BodyProps = {
	body: string;
	bodyKind: ExchangeMessageBodyKind;
	shouldAllowUnsecureContent: boolean;
	shouldAutomaticallyShowSenderImages: boolean;
	onUnsecureContent(): void;
	onInlineImagesDetected(): void;
	onPointerEvent?(): void;
};

export type BodyHandle = {
	loadInlineImages(): Promise<void>;
	getContent(): string;
};

export const Body = forwardRef(
	(props: BodyProps, ref: Ref<BodyHandle>): ReactElement => {
		const {
			body,
			bodyKind,
			shouldAllowUnsecureContent,
			shouldAutomaticallyShowSenderImages,
			onUnsecureContent,
			onInlineImagesDetected,
			onPointerEvent
		} = props;

		const htmlInFrameRef: RefObject<HtmlInFrameHandle | null> = useRef<HtmlInFrameHandle>(null);

		// NOTE potentially a state could be used to hold frameWindowRef, instead of ref + version state
		// But reference of the window rendered in frame does not change when entire content is rerendered
		// And because of that all the effect do not run, when frame's content is updated
		const frameWindowRef: RefObject<Window | null> = useRef<Window>(null);
		const [notifyAboutFrameWindowUpdate, frameWindowVersion] = useForceRerender();
		const cancelPreviousSecurityViolationEventHandlers: () => AbortSignal = useCancelPreviousExecution();

		const preparedBody = useMemo(
			(): string => {
				if (!body) {
					return "";
				}

				let result: string = body;

				switch (bodyKind) {
					case ExchangeMessageBodyKind.Text:
						result = convertPlainTextBodyToHtml(result);
						break;
					case ExchangeMessageBodyKind.Html:
						result = adjustTargetAndRelForAnchors(result);
						result = sanitizeHtml(
							result,
							{ globalStylesBehavior: "keep" }
						);
						break;
					default:
						never(bodyKind);
				}

				return result;
			},
			[
				body,
				bodyKind
			]
		);

		const loadInlineImages = useCallback(
			async (): Promise<void> => {
				const frameWindow: Window | null = frameWindowRef.current;

				if (!frameWindow) {
					return;
				}

				const inlineImages: NodeListOf<HTMLImageElement> = frameWindow.document
					.querySelectorAll<HTMLImageElement>("img[data-src]");

				const loadInlineImage = (image: HTMLImageElement): Promise<void> => {
					return new Promise(
						(resolve, reject) => {
							const relativeUrl: string = ensureNotNil(image.getAttribute("data-src"));

							image.setAttribute("src", createUrlRelativeToEnvironment(relativeUrl));

							const loadHandler = (): void => {
								resolve();
								image.removeEventListener("load", loadHandler);
								image.removeEventListener("error", errorHandler);
							};

							const errorHandler = (ev: ErrorEvent): void => {
								reject(ev.error);
								image.removeEventListener("load", loadHandler);
								image.removeEventListener("error", errorHandler);
							};

							image.addEventListener("load", loadHandler);
							image.addEventListener("error", errorHandler);
						}
					);
				};

				const loadImagePromises: Promise<void>[] = [];

				inlineImages.forEach(
					inlineImage => {
						loadImagePromises.push(
							loadInlineImage(inlineImage)
						);
					}
				);

				await Promise.allSettled(loadImagePromises);
			},
			[]
		);

		useEffect(
			() => {
				hasImplicitDependencyOn(frameWindowVersion);

				const frameWindow: Window | null = frameWindowRef.current;

				if (frameWindow && shouldAutomaticallyShowSenderImages) {
					loadInlineImages();
				}
			},
			[
				loadInlineImages,
				shouldAutomaticallyShowSenderImages,
				frameWindowVersion
			]
		);

		useEffect(
			(): void => {
				hasImplicitDependencyOn(frameWindowVersion);

				const frameWindow: Window | null = frameWindowRef.current;

				if (!frameWindow) {
					return;
				}

				const inlineImage: HTMLImageElement | null = frameWindow.document
					.querySelector<HTMLImageElement>("img[data-src]");

				if (isNotNil(inlineImage)) {
					onInlineImagesDetected();
				}
			},
			[frameWindowVersion, onInlineImagesDetected]
		);

		useEffect(
			() => {
				hasImplicitDependencyOn(frameWindowVersion);
				if (!onPointerEvent) {
					return undefined;
				}
				const frameWindow: Window | null = frameWindowRef.current;

				if (!frameWindow) {
					return undefined;
				}

				const handler = (): void => onPointerEvent();
				const frameDocument: Document = frameWindow.document;
				frameDocument.addEventListener("mousedown", handler);
				frameDocument.addEventListener("contextmenu", handler);
				frameDocument.addEventListener("touchmove", handler);
				frameDocument.addEventListener("wheel", handler);

				return () => {
					frameDocument.removeEventListener("mousedown", handler);
					frameDocument.removeEventListener("contextmenu", handler);
					frameDocument.removeEventListener("touchmove", handler);
					frameDocument.removeEventListener("wheel", handler);
				};
			},
			[frameWindowVersion, onPointerEvent]
		);

		const getContent = useCallback(
			(): string => {
				const frameWindow: Window | null = frameWindowRef.current;

				if (!frameWindow) {
					throw new Error("Cannot get message contents Frame window is not set.");
				}

				return frameWindow.document
					.body
					.innerHTML;
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): BodyHandle => ({
				loadInlineImages,
				getContent
			}),
			[loadInlineImages, getContent]
		);

		const frameWindowChangeHandler = useCallback(
			(frameWindow: Window): void => {
				frameWindowRef.current = frameWindow;
				notifyAboutFrameWindowUpdate();

				const newCancellationSignal: AbortSignal = cancelPreviousSecurityViolationEventHandlers();

				// NOTE: Probably due some kind of race condition in firefox the event sometimes fires
				// on the top window/document instead of the iframe
				window.addEventListener(
					"securitypolicyviolation",
					(ev) => {
						if (ev.originalPolicy
							&& ev.originalPolicy.includes(policyIdentifier)
						) {
							onUnsecureContent();
						}
					},
					{
						signal: newCancellationSignal
					}
				);

				if (frameWindow) {
					frameWindow.addEventListener(
						"securitypolicyviolation",
						() => onUnsecureContent(),
						{
							signal: newCancellationSignal
						}
					);
				}
			},
			[
				notifyAboutFrameWindowUpdate,
				onUnsecureContent,
				cancelPreviousSecurityViolationEventHandlers
			]
		);

		useEffect(
			() => {
				hasImplicitDependencyOn(shouldAllowUnsecureContent);

				// Content security policy is only applied once to a frame during initial render
				// so to change it we need to completely re-render frame and not just update its contents
				htmlInFrameRef.current
					?.reInitializeFrame();
			},
			[shouldAllowUnsecureContent]
		);

		const trackingId: string = "body";

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				<HtmlInFrame
					ref={htmlInFrameRef}
					frameWindowRef={frameWindowChangeHandler}
					body={preparedBody}
					head={defaultStylesHeader + (shouldAllowUnsecureContent ? "" : forbidNonSecureContentHeader)}
					size={{
						horizontal: "stretch",
						vertical: "content-size"
					}}
					trackingId={generateTrackingId(trackingId, "__iframe")}
				/>
			</div>
		);
	}
);
Body.displayName = "Body";

const defaultStylesHeader = `<style>
img {
	width:auto;
}
body{
	box-sizing: border-box;
	font-size: 12px;
	font-family: Verdana,Geneva,sans-serif;
	word-wrap: break-word;
	-webkit-nbsp-mode: space;
	-webkit-line-break: after-white-space;
}
ul,ol{
	padding-left:2.5em
}
p {
	margin: 0;
	min-height: 1em;
}
</style>`;

const policyIdentifier: string = newGuid();
const allowInlineStyles: string = "style-src 'unsafe-inline'";
const forbidAnyScripts: string = `script-src 'none' 'nonce-${policyIdentifier}'`;
const allowHttpsUrlsOnlyByDefault: string = "default-src https://*";
export const forbidNonSecureContentHeader = `<meta http-equiv="Content-Security-Policy" content="${allowInlineStyles}; ${forbidAnyScripts}; ${allowHttpsUrlsOnlyByDefault};">`;

function useCancelPreviousExecution(): (() => AbortSignal) {
	const cancellationRef: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);

	return useCallback(
		(): AbortSignal => {
			if (cancellationRef.current) {
				cancellationRef.current
					.abort();
			}

			cancellationRef.current = new AbortController();

			return cancellationRef.current
				.signal;
		},
		[]
	);
};